import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  getUserDetail, 
  suspendUser, 
  activateUser, 
  approveSeller, 
  rejectSeller, 
  suspendSeller 
} from '../../services/api';

function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchUserDetail();
  }, [userId]);

  const fetchUserDetail = async () => {
    try {
      setLoading(true);
      const response = await getUserDetail(userId);
      setUser(response.data);
    } catch (err) {
      setError('Failed to load user details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = async (action, reason = '') => {
    if (!window.confirm(`Are you sure you want to ${action.replace('_', ' ')} this user?`)) {
      return;
    }

    try {
      setActionLoading(true);
      
      switch (action) {
        case 'suspend':
          await suspendUser(userId, reason);
          break;
        case 'activate':
          await activateUser(userId);
          break;
        case 'approve_seller':
          await approveSeller(user.seller_id);
          break;
        case 'reject_seller':
          await rejectSeller(user.seller_id, reason);
          setShowRejectModal(false);
          setRejectionReason('');
          break;
        case 'suspend_seller':
          await suspendSeller(user.seller_id, reason);
          break;
        default:
          break;
      }
      
      await fetchUserDetail();
    } catch (err) {
      setError(`Failed to ${action.replace('_', ' ')}. Please try again.`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status, type = 'user') => {
    if (type === 'seller') {
      switch (status) {
        case 'active':
          return (
            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-900/30 text-green-400 border border-green-700/50">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
              Active
            </div>
          );
        case 'suspended':
          return (
            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-900/30 text-red-400 border border-red-700/50">
              <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
              Suspended
            </div>
          );
        case 'inactive':
          return (
            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-700/50 text-gray-300 border border-gray-600/50">
              <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
              Inactive
            </div>
          );
        default:
          return (
            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-700/50 text-gray-300 border border-gray-600/50">
              {status}
            </div>
          );
      }
    }

    // User status
    if (user?.deleted_at) {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-900/30 text-red-400 border border-red-700/50">
          <div className="w-2 h-2 bg-red-400 rounded-full mr-2"></div>
          Suspended
        </div>
      );
    }
    return (
      <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-900/30 text-green-400 border border-green-700/50">
        <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
        Active
      </div>
    );
  };

  const getVerificationBadge = (isVerified) => {
    return isVerified ? (
      <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-900/30 text-green-400 border border-green-700/50">
        <span className="mr-1.5">✓</span>
        Verified
      </div>
    ) : (
      <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-900/30 text-yellow-400 border border-yellow-700/50">
        <span className="mr-1.5">⚠</span>
        Unverified
      </div>
    );
  };

  const getNameVerificationBadge = (nameVerified) => {
    return nameVerified ? (
      <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-900/30 text-green-400 border border-green-700/50">
        <span className="mr-1.5">✓</span>
        Name Verified
      </div>
    ) : (
      <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-900/30 text-yellow-400 border border-yellow-700/50">
        <span className="mr-1.5">⚠</span>
        Name Not Verified
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-300 mb-2">Error Loading User</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/admin/users')}
            className="bg-gradient-to-r from-[#ff660e] to-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 hover:scale-[1.02] shadow-lg"
          >
            ← Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 shadow-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center py-6 space-y-4 md:space-y-0">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Link 
                  to="/admin/users"
                  className="text-blue-400 hover:text-blue-300 transition-colors flex items-center space-x-2"
                >
                  <span>←</span>
                  <span>Back to Users</span>
                </Link>
              </div>
              <h1 className="text-3xl font-bold text-white">
                User <span className="text-[#ff660e]">Details</span>
              </h1>
              <p className="text-gray-400 mt-1">Manage user account and permissions</p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex space-x-3">
              {user?.deleted_at ? (
                <button
                  onClick={() => handleUserAction('activate')}
                  disabled={actionLoading}
                  className="bg-gradient-to-r from-green-600 to-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-700 hover:to-emerald-600 transition-all duration-200 hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <span className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Activating...</span>
                    </span>
                  ) : (
                    <span className="flex items-center space-x-2">
                      <span>✅</span>
                      <span>Activate User</span>
                    </span>
                  )}
                </button>
              ) : (
                <button
                  onClick={() => handleUserAction('suspend', 'Administrative action')}
                  disabled={actionLoading}
                  className="bg-gradient-to-r from-red-600 to-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-red-700 hover:to-red-600 transition-all duration-200 hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <span className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Suspending...</span>
                    </span>
                  ) : (
                    <span className="flex items-center space-x-2">
                      <span>⏸️</span>
                      <span>Suspend User</span>
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 flex items-center justify-between backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <span className="text-red-400 text-xl">⚠️</span>
              <p className="text-red-300">{error}</p>
            </div>
            <button 
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-300 font-medium transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-8">
            {/* Profile Card */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-4">
                  {user?.profile_picture_url ? (
                    <img
                      className="h-20 w-20 rounded-full ring-2 ring-gray-700"
                      src={user.profile_picture_url}
                      alt={`${user.first_name} ${user.last_name}`}
                    />
                  ) : (
                    <div className="h-20 w-20 bg-gradient-to-br from-[#ff660e] to-orange-400 rounded-full flex items-center justify-center text-white font-semibold text-2xl ring-2 ring-gray-700">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      {user?.first_name} {user.middle_name} {user?.last_name}
                    </h2>
                    <p className="text-gray-400">{user?.email}</p>
                    <div className="flex items-center space-x-3 mt-3">
                      {getStatusBadge()}
                      {getNameVerificationBadge(user?.name_verified)}
                      {user?.is_seller && (
                        <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-blue-900/30 to-indigo-900/30 text-blue-400 border border-blue-700/50">
                          <span className="mr-1.5">🏪</span>
                          Seller
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="mr-2 text-[#ff660e]">👤</span>
                    Personal Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-400">Email</label>
                      <p className="text-white text-lg">{user?.email}</p>
                      <div className="mt-2">
                        {getVerificationBadge(user?.is_email_verified)}
                      </div>
                    </div>
                    {user?.phone_number && (
                      <div>
                        <label className="text-sm font-medium text-gray-400">Phone</label>
                        <p className="text-white text-lg">{user.phone_number}</p>
                        <div className="mt-2">
                          {getVerificationBadge(user?.is_phone_verified)}
                        </div>
                      </div>
                    )}
                    
                    {/* Name Verification Section */}
                    <div className="border-t border-gray-700 pt-4 mt-4">
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-gray-400">Name Verification</label>
                        {getNameVerificationBadge(user?.name_verified)}
                      </div>
                      {user?.name_verified && user?.verified_name && (
                        <div>
                          <label className="text-sm font-medium text-gray-400">Verified Name</label>
                          <p className="text-white">{user.verified_name}</p>
                          {user?.name_verification_date && (
                            <p className="text-sm text-gray-500 mt-1">
                              Verified on: {new Date(user.name_verification_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Location Information */}
                    <div className="border-t border-gray-700 pt-4 mt-4">
                      <label className="text-sm font-medium text-gray-400 mb-2 block">Location Details</label>
                      <div className="space-y-2">
                        {user?.state_name && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">State:</span>
                            <span className="text-white">{user.state_name}</span>
                          </div>
                        )}
                        {user?.lga_name && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">LGA:</span>
                            <span className="text-white">{user.lga_name}</span>
                          </div>
                        )}
                        {user?.lcda_name && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">LCDA:</span>
                            <span className="text-white">{user.lcda_name}</span>
                          </div>
                        )}
                        {user?.state_region && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Region:</span>
                            <span className="text-white">{user.state_region}</span>
                          </div>
                        )}
                        {user?.state_capital && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Capital:</span>
                            <span className="text-white">{user.state_capital}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-gray-700 pt-4 mt-4">
                      <div className="flex justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-400">Member Since</label>
                          <p className="text-white">
                            {new Date(user?.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-400">Last Updated</label>
                          <p className="text-white">
                            {new Date(user?.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      {user?.deleted_at && (
                        <div className="mt-4">
                          <label className="text-sm font-medium text-gray-400">Suspended Since</label>
                          <p className="text-red-400">
                            {new Date(user.deleted_at).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {user?.deleted_by && (
                        <div className="mt-2">
                          <label className="text-sm font-medium text-gray-400">Suspended By</label>
                          <p className="text-red-400">Admin ID: {user.deleted_by}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Account Statistics */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <span className="mr-2 text-[#ff660e]">📊</span>
                    Account Statistics
                  </h3>
                  <div className="space-y-4">
                    {/* User Rating */}
                    <div className="bg-gray-750 border border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-400">User Rating</span>
                        <span className="text-2xl font-bold text-yellow-400">
                          {user?.rating_avg && typeof user.rating_avg === 'number' ? user.rating_avg.toFixed(1) : 'N/A'} ⭐
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-400">
                        Based on user feedback and reviews
                      </div>
                    </div>

                    {/* Statistics from API */}
                    <div className="bg-gray-750 border border-gray-700 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-400 mb-3">Activity Statistics</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-300">Orders as Buyer</span>
                          <span className="text-xl font-bold text-white">
                            {user?.statistics?.orders_as_buyer || 0}
                          </span>
                        </div>
                        {user?.is_seller && (
                          <>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-300">Listings Created</span>
                              <span className="text-xl font-bold text-white">
                                {user?.statistics?.listing_count || 0}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-300">Total Sales</span>
                              <span className="text-xl font-bold text-green-400">
                                ₦{(user?.statistics?.total_sales || 0).toLocaleString()}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Additional Stats Section */}
                    <div className="bg-gray-750 border border-gray-700 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-400 mb-3">Additional Info</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-300">User ID</span>
                          <span className="text-sm font-mono text-white">{user?.user_id}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-300">State ID</span>
                          <span className="text-sm text-white">{user?.state_id}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-300">LGA ID</span>
                          <span className="text-sm text-white">{user?.lga_id}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-300">LCDA ID</span>
                          <span className="text-sm text-white">{user?.lcda_id}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
              
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-8">
            {/* Account Status */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-2 text-[#ff660e]">📋</span>
                Account Status
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">User Status</span>
                  {getStatusBadge()}
                </div>
                {user?.is_seller && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-400">Seller Status</span>
                    {getStatusBadge(user?.seller_status, 'seller')}
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Email Verified</span>
                  {getVerificationBadge(user?.is_email_verified)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Phone Verified</span>
                  {getVerificationBadge(user?.is_phone_verified)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-400">Name Verified</span>
                  {getNameVerificationBadge(user?.name_verified)}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
                <span className="mr-2 text-[#ff660e]">⚡</span>
                Quick Actions
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate(`/admin/orders?user_id=${userId}`)}
                  className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                >
                  <span className="text-lg">📦</span>
                  <span>View User Orders</span>
                </button>
                {user?.is_seller && (
                  <button
                    onClick={() => navigate(`/admin/listings?seller_id=${user.seller_id}`)}
                    className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                  >
                    <span className="text-lg">🏪</span>
                    <span>View Seller Listings</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(userId);
                    alert('User ID copied to clipboard!');
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                >
                  <span className="text-lg">📋</span>
                  <span>Copy User ID</span>
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(user?.email);
                    alert('Email copied to clipboard!');
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                >
                  <span className="text-lg">📧</span>
                  <span>Copy Email</span>
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(JSON.stringify(user, null, 2));
                    alert('User data copied to clipboard!');
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-300 hover:bg-gray-750 hover:text-white rounded-lg transition-all duration-200 hover:scale-[1.02] border border-gray-700 hover:border-[#ff660e] flex items-center space-x-3"
                >
                  <span className="text-lg">📄</span>
                  <span>Copy User Data</span>
                </button>
              </div>
            </div>

            
          </div>
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-xl border border-gray-700 max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center">
              <span className="text-red-400 mr-2">❌</span>
              Reject Seller Application
            </h3>
            <p className="text-gray-400 mb-6">Please provide a reason for rejecting this seller application:</p>
            
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:ring-2 focus:ring-[#ff660e] focus:border-transparent transition-all mb-6"
              rows="4"
            />
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="px-5 py-2.5 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700 hover:text-white transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUserAction('reject_seller', rejectionReason)}
                disabled={!rejectionReason.trim() || actionLoading}
                className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-lg font-semibold hover:from-red-700 hover:to-red-600 transition-all duration-200 hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading ? (
                  <span className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Rejecting...</span>
                  </span>
                ) : 'Reject Application'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetail;